import * as React from 'react';
import { useSelector } from 'react-redux';
import * as ReactDOMServer from 'react-dom/server';
import { IMapGeoObject } from '@cian/frontend-newbuilding-map-component';
import { TCustomBalloonFetchHtmlFunction } from '@cian/frontend-newbuilding-map-component/es/types';
import { debounce } from '@cian/newbuilding-utils';
import { Map as ProjectsMapComponent } from '../../components/Map';
import {
  checkFsk,
  getDeveloperId,
  getFilterRegionId,
  getMapApiKey,
  getNewBuildings,
  getOffices,
  getRegionCenterCoordinates,
} from '../../selectors';
import { TViewMode } from '../ProjectsOnMap/ProjectsOnMap';
import { NewBuildingMapBalloon } from '../NewBuildingMapBalloon';
import { getPins } from './helpers';
import { OfficeMapBalloon } from '../OfficeMapBalloon';
import { trackMapPinClick, trackSeeAllClick, trackShowPhoneBalloon } from './tracking';
import { prepareTrackingEvent } from './helpers/prepareTrackingEvent';
import { handleOfficePhone } from './helpers/handleOfficePhone';
import { handleSeeAllLink } from './helpers/handleSeeAllLink';
import { handleNewbuildingPhone } from './helpers/handleNewbuildingPhone';

interface IProjectsMapProps {
  mode: TViewMode;
}

export function Map({ mode }: IProjectsMapProps) {
  const apiKey = useSelector(getMapApiKey);
  const developerId = useSelector(getDeveloperId);
  const isFsk = useSelector(checkFsk);
  const {
    data: { hash, list },
  } = useSelector(getNewBuildings);
  const offices = useSelector(getOffices);
  const { center, zoom } = useSelector(getRegionCenterCoordinates);
  const regionId = useSelector(getFilterRegionId);

  const newBuildingsIds = React.useMemo(() => list.map(newBuilding => String(newBuilding.id)), [list]);
  const officesIds = React.useMemo(() => offices.list.map(office => office.id), [offices.list]);
  const [pinTypes, setPinTypes] = React.useState<string[]>([]);

  const handleGeoObjectsFirstFetch = (): Promise<IMapGeoObject[]> => {
    const pins = getPins(list, offices.list);

    return Promise.resolve(pins);
  };

  const handleNewBuildingBalloon = React.useCallback(
    (geoObject: IMapGeoObject) => {
      const newBuilding = hash[geoObject.options.id];

      if (!newBuilding) {
        return { html: Promise.reject(new Error('Не верный тип пина!')) };
      }

      return {
        html: Promise.resolve(
          ReactDOMServer.renderToStaticMarkup(
            <NewBuildingMapBalloon
              newBuilding={newBuilding}
              callbackButtonTheme={isFsk ? 'fsk_fill_primary' : undefined}
            />,
          ),
        ),
        closeSelector: '#close',
      };
    },
    [hash, isFsk],
  );

  const handleOfficeBalloon = React.useCallback(
    (geoObject: IMapGeoObject) => {
      const office = offices.hash[geoObject.options.id];

      if (!office) {
        return { html: Promise.reject(new Error('Не верный тип пина!')) };
      }

      return {
        html: Promise.resolve(
          ReactDOMServer.renderToStaticMarkup(
            <OfficeMapBalloon office={office} callbackButtonTheme={isFsk ? 'fsk_fill_primary' : undefined} />,
          ),
        ),
        closeSelector: '#close',
      };
    },
    [isFsk, offices.hash],
  );
  const handleBalloonHtmlFirstFetchMap: Record<TViewMode, TCustomBalloonFetchHtmlFunction> = React.useMemo(
    () => ({
      newBuildings: handleNewBuildingBalloon,
      offices: handleOfficeBalloon,
    }),
    [handleNewBuildingBalloon, handleOfficeBalloon],
  );

  const handleMapPinClick = React.useCallback(
    ({ options: { id } }: IMapGeoObject) => {
      const event = prepareTrackingEvent({
        mode,
        geoId: id,
        newbuildings: list,
        offices: offices.list,
      });
      const onShowNewbuildingPhoneClick = () =>
        trackShowPhoneBalloon({
          newbuilding: hash[event.id],
          regionId,
          position: event.position,
          developerId,
        });

      const onSeeAllLink = () =>
        trackSeeAllClick({
          newbuilding: hash[event.id],
          regionId,
          position: event.position,
          developerId,
        });

      trackMapPinClick({
        pinType: mode,
        newbuilding: hash[event.id],
        regionId,
        position: event.position,
        developerId,
      });
      handleNewbuildingPhone({ mode, newbuilding: hash[event.id], onShowPhoneClick: onShowNewbuildingPhoneClick });
      handleOfficePhone({
        mode,
        officeId: id,
        officesHash: offices.hash,
      });
      handleSeeAllLink(mode, onSeeAllLink);
    },
    [mode, list, offices, developerId, hash, regionId],
  );

  const debouncedHandleMapPinClick = debounce(200, handleMapPinClick);

  React.useEffect(() => {
    const pinsMap: Record<TViewMode, string[]> = {
      newBuildings: newBuildingsIds,
      offices: officesIds,
    };

    setPinTypes(pinsMap[mode]);
  }, [mode, newBuildingsIds, officesIds]);

  return (
    <ProjectsMapComponent
      onGeoObjectClick={debouncedHandleMapPinClick}
      apiKey={apiKey}
      pinCoordinates={center}
      zoom={zoom}
      currentTypes={pinTypes}
      onGeoObjectsFirstFetch={handleGeoObjectsFirstFetch}
      onCustomBalloonHtmlFetch={handleBalloonHtmlFirstFetchMap[mode]}
    />
  );
}
